import { useItemContext } from '@weenat/client'
import { toDataPoint } from '@weenat/client/dist/core/charts'
import { DataPoint } from '@weenat/client/dist/core/charts/d3-charts.type'
import { getColorScalePerMetric } from '@weenat/client/dist/core/measurements'
import { fromWindAngleToDirection, useMetricSummaryChart } from '@weenat/client/dist/core/metrics'
import { UseMetricSummaryChartInterface } from '@weenat/client/dist/core/metrics/useMetricSummaryChart'
import { TimeSteps } from '@weenat/client/dist/enums/TimeSteps'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { DeviceAvailableMeasure } from '@weenat/client/dist/resources/devices'
import { PlotAvailableMeasure } from '@weenat/client/dist/resources/plots'
import { useIntl } from '@weenat/wintl'
import { useIsAppAccessRestricted } from 'app/src/hooks/useIsAppAccessRestricted'
import Axis from 'app/src/kit/d3-charts/Axis'
import Bar from 'app/src/kit/d3-charts/Bar'
import CursorChart from 'app/src/kit/d3-charts/CursorChart'
import Line from 'app/src/kit/d3-charts/Line'
import TimeAxis from 'app/src/kit/d3-charts/TimeAxis'
import { scaleLinear } from 'd3-scale'
import { isNil } from 'lodash-es'
import isEmpty from 'lodash-es/isEmpty'
import moment from 'moment-timezone'
import { useCallback, useMemo } from 'react'
import Text from '../../Text'
import ChartWrapper from '../../d3-charts/ChartWrapper'
import DefaultChartTooltipContent from '../../d3-charts/DefaultChartTooltipContent'
import FutureArea from '../../d3-charts/FutureArea'
import RestrictedArea from '../../d3-charts/RestrictedArea'
import SolarRadiationThresholdLine from '../../d3-charts/SolarRadiationThresholdLine'
import WindDirectionGutter, {
  WIND_DIRECTION_GUTTER_HEIGHT
} from '../../d3-charts/WindDirectionGutter'
import WindDirectionIcon from '../../icons/WindDirectionIcon'

const CHART_HEIGHT = 148

const domainPadding = { bottom: 0, left: 4, right: 4, top: 16 }

interface ChartProps {
  metric: DeviceAvailableMeasure | PlotAvailableMeasure
  prefetch?: UseMetricSummaryChartInterface['prefetch']
  height?: number
  variant?: 'lg' | 'sm'
  showFutureArea?: boolean
  customColorScale?: (domain: number[]) => (value: number) => string
  isVWS?: boolean
}

/**
 * Chart used inside summaries
 * @deprecated remove with sdh
 */
const MetricSummaryChart: FC<ChartProps> = ({
  metric,
  prefetch,
  height,
  variant = 'lg',
  showFutureArea = true,
  customColorScale = undefined,
  isVWS
}) => {
  const { timezone } = useItemContext()
  const { convertValue, formatConvertedValue, getUnit } = useConvertedValue()
  const { t } = useIntl()
  const { isRestricted } = useIsAppAccessRestricted({
    isVWS
  })

  const { data, isLoading, windDirectionData, xDomain, denormalizeFct, getGradient, yDomain } =
    useMetricSummaryChart({
      metric,
      prefetch
    })

  const colorScale = !isNil(customColorScale) ? customColorScale : getColorScalePerMetric(metric)

  const d3Data = useMemo(() => data.map(toDataPoint), [data])
  const d3WindDirection = useMemo(() => windDirectionData.map(toDataPoint), [windDirectionData])

  const ChartContent = useMemo(() => {
    switch (metric) {
      case 'T':
      case 'U':
      case 'T_SOIL':
      case 'SSI':
      case 'PPFD':
      case 'LW_V':
      case 'LW_D':
      case 'T_DRY':
      case 'T_WET':
        return (
          <>
            {metric === 'SSI' ? (
              <SolarRadiationThresholdLine
                denormalizeFct={denormalizeFct}
                normalizeFct={scaleLinear().range([0, 1]).domain(yDomain)}
                variant={variant}
              />
            ) : null}
            <Line
              data={d3Data}
              metric={metric}
              denormalize={denormalizeFct}
              getGradient={getGradient}
              timeStep={TimeSteps.perHours}
              dataUnit={getUnit(metric)}
            />
          </>
        )
      case 'RR':
        return <Bar data={d3Data} barWidth={6} $color={colorScale([0, 1])} />
      case 'FF':
        return (
          <>
            <WindDirectionGutter data={d3WindDirection} />
            <Line
              data={d3Data}
              metric={metric}
              denormalize={denormalizeFct}
              timeStep={TimeSteps.perHours}
            />
          </>
        )

      default:
        return null
    }
  }, [
    metric,
    denormalizeFct,
    yDomain,
    variant,
    d3Data,
    getGradient,
    getUnit,
    colorScale,
    d3WindDirection
  ])

  const renderTooltip = useCallback(
    ({ date, value }: DataPoint) => {
      let windDirectionValue: number | null | undefined = undefined
      if (metric === 'FF') {
        windDirectionValue = windDirectionData.find((d) => d.x.getTime() === date.getTime())?.value
      }
      return (
        <DefaultChartTooltipContent>
          <Text>{moment(date).format('DD/MM HH:mm')}</Text>
          <Text $color={`metrics.${metric}.500`}>
            {metric === 'LW_V' || metric == 'LW_D'
              ? formatConvertedValue({
                  metric,
                  value: denormalizeFct(value),
                  showEmptyState: true,
                  maximumFractionDigits: metric === 'LW_V' ? undefined : 2
                })
              : convertValue({ metric, value: denormalizeFct(value), showEmptyState: true })}
          </Text>
          {metric === 'FF' && !isNil(windDirectionValue) ? (
            <Flex $alignItems='center'>
              <WindDirectionIcon
                windOrigin={windDirectionValue}
                $color={'metrics.FF.500'}
                $size={'rg'}
              />
              <Text $fontSize='sm'>
                {t(`windDirections.${fromWindAngleToDirection(windDirectionValue)}`)}
              </Text>
            </Flex>
          ) : null}
        </DefaultChartTooltipContent>
      )
    },
    [convertValue, denormalizeFct, formatConvertedValue, metric, t, windDirectionData]
  )

  const domain = { x: xDomain, y: [0, 1] }
  const chartHeight = height ?? CHART_HEIGHT

  return !isNil(data) ? (
    <ChartWrapper height={chartHeight} isEmpty={isEmpty(data)} isLoading={isLoading}>
      <CursorChart
        height={chartHeight}
        width='100%'
        domain={domain}
        domainPadding={domainPadding}
        data={d3Data}
        renderTooltip={renderTooltip}
        timeStep={TimeSteps.perHours}
        marginTop={metric === 'FF' ? WIND_DIRECTION_GUTTER_HEIGHT : 8}
        marginBottom={24}
        marginRight={16}
        isChartRestricted={isRestricted}
      >
        {showFutureArea && <FutureArea />}
        {ChartContent}
        <Axis denormalize={denormalizeFct} metric={metric} />
        <TimeAxis timezone={timezone} period={xDomain} />
        {isRestricted && <RestrictedArea />}
      </CursorChart>
    </ChartWrapper>
  ) : null
}

export default MetricSummaryChart
